import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// V1-launch (2026-10-01): real app screenshots (captured from the running app) replace emoji visuals.
import shotQr from '../assets/landing/app-acceso-qr.webp';
import shotFinanzas from '../assets/landing/app-reportes-financieros.webp';
import shotComunidad from '../assets/landing/app-comunidad.webp';
import shotPagos from '../assets/landing/app-pagos.webp';

// ─── Feature Data ─────────────────────────────────────────────────────────────
// visual = REAL app screenshot captured from the running product (no emoji, no mocked UI).
const features = [
  {
    tag: 'Acceso',
    headline: 'Tu condominio,\ntu código QR.',
    body: 'Olvídate de las llaves. Accede con tu teléfono, recibe visitantes sin complicaciones y genera códigos temporales en segundos.',
    visual: shotQr,
    alt: 'Control de visitantes de Vecindus: registro de entradas y salidas',
  },
  {
    tag: 'Transparencia',
    headline: 'Reportes financieros\nen tiempo real.',
    body: 'Cada peso entra y sale documentado. Tus residentes ven los estados de cuenta claros; los morosos quedan visibles para todos.',
    visual: shotFinanzas,
    alt: 'Reportes financieros de Vecindus: ingresos y egresos del condominio',
  },
  {
    tag: 'Comunidad',
    headline: 'Votaciones, eventos\ny avisos en un solo lugar.',
    body: 'Olvídate del WhatsApp. Anuncios oficiales, encuestas con quórum y calendario communal: todo organizado, todo a la vista.',
    visual: shotComunidad,
    alt: 'Módulo de comunidad de Vecindus: votaciones, avisos y calendario',
  },
  {
    tag: 'Cobranza',
    headline: 'Cobra en línea\nsin correr tras nadie.',
    body: 'Openpay integrado. Cargos automáticos, recordatorios suaves y historial completo de pagos. El dinero llega sin perseguirlo.',
    visual: shotPagos,
    alt: 'Módulo de pagos de Vecindus: cargos pendientes e historial de pagos',
  },
];

const testimonials = [
  { quote: 'Antes gastábamos 4 horas al mes en reportes. Ahora se hacen solos.', author: 'María G.', role: 'Administradora, CDMX' },
  { quote: 'Mis residentes ahora pagan a tiempo porque ven exactamente a dónde va su dinero.', author: 'Carlos R.', role: 'Presidente de condomino, Monterrey' },
  { quote: 'La villa estaba en crisis y todo era un desorden. Vecindus nos dio orden en 2 semanas.', author: 'Ana L.', role: 'Directora, Bogotá', highlight: true },
];

// ─── V1 Launch: plan comparison (modules per user type, like the original site) ───
// Source: original MiResidenciApp site (archived). Prices intentionally NOT shown for future plans.
type PlanColumn = { id: string; name: string; status: 'disponible' | 'proximamente'; blurb: string; users: { type: string; items: string[] }[] };
const planColumns: PlanColumn[] = [
  {
    id: 'gratis', name: 'Plan Gratis', status: 'disponible', blurb: 'Disponible hoy para tu condominio.',
    users: [
      { type: 'Residentes', items: ['Comunicación Administrador–Residente', 'Quejas y sugerencias', 'Votaciones', 'Vista de calendario', 'Apartado y pago de amenidades', 'Información general', 'Pagos de mantenimiento', 'Notificación de avisos', 'Solicitar proveedor', 'Crear su propio usuario proveedor'] },
      { type: 'Administrativo', items: ['Comunicación Administrador–Residente', 'Quejas y sugerencias', 'Votaciones', 'Vista y modificación del calendario', 'Apartado y pago de amenidades', 'Modificación de información general', 'Pagos de mantenimiento', 'Creación de avisos', 'Solicitar proveedor'] },
      { type: 'Seguridad', items: ['Disponibilidad de amenidades', 'Vista de calendario', 'Información general'] },
      { type: 'Proveedores', items: ['Proveedor dentro de su fraccionamiento/edificio', 'Recibir pagos (TC, débito o transferencia)', 'Recibir pedidos', 'Menú de productos'] },
    ],
  },
  {
    id: 'basico', name: 'Plan Básico', status: 'proximamente', blurb: 'Todo lo del plan Gratis, más acceso y finanzas.',
    users: [
      { type: 'Residentes', items: ['Todo lo del plan Gratis', 'Acceso con QR (residentes e invitados)', 'Vista de morosos', 'Proyectos y presupuestos', 'Reporte mensual y anual de ingresos y egresos'] },
      { type: 'Administrativo', items: ['Todo lo del plan Gratis', 'Acceso con QR (residentes e invitados)', 'Vista de morosos', 'Proyectos y presupuestos', 'Reporte mensual y anual de ingresos y egresos', 'Administración económica de gastos'] },
      { type: 'Seguridad', items: ['Todo lo del plan Gratis', 'Lector QR para residentes e invitados', 'Botones de pánico SOS', 'Alerta de invitado'] },
      { type: 'Proveedores', items: ['Todo lo del plan Gratis', 'Proveedor dentro de la ciudad', 'Fotos en el menú de productos'] },
    ],
  },
  {
    id: 'platinum', name: 'Plan Platinum', status: 'proximamente', blurb: 'Gobierno del condominio y contabilidad completa.',
    users: [
      { type: 'Residentes', items: ['Todo lo del plan Básico'] },
      { type: 'Administrativo', items: ['Todo lo del plan Básico', 'Funciones de Mesa Directiva (votaciones internas, calendario)', 'Declaración anual contable del fraccionamiento/edificio'] },
      { type: 'Seguridad', items: ['Todo lo del plan Básico'] },
      { type: 'Proveedores', items: ['Todo lo del plan Básico'] },
    ],
  },
];

const roadmap = [
  { label: 'Hoy', title: 'Plan Gratis', detail: 'Disponible para todos los condominios.', tone: 'done' as const },
  { label: 'Próximamente', title: 'Plan Básico', detail: 'Acceso QR, morosos y reportes financieros.', tone: 'next' as const },
  { label: 'Después', title: 'Plan Platinum', detail: 'Mesa directiva y contabilidad anual.', tone: 'later' as const },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const { t } = useTranslation('common');
  const [isLoaded, setIsLoaded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Launch-readiness item 1 (resumed 2026-10-01): pre-launch waiting list capture.
  // Contract (parallel feat/waitlist-api lane): POST /api/waitlist { email }
  //   -> 201 success | 409 duplicate | 400 invalid email. Plain 4xx handling only.
  const [wlEmail, setWlEmail] = useState('');
  const [wlStatus, setWlStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [wlMessage, setWlMessage] = useState('');

  async function submitWaitingList(e: React.FormEvent) {
    e.preventDefault();
    if (wlStatus === 'sending') return;
    setWlStatus('sending');
    setWlMessage('');
    try {
      const r = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: wlEmail.trim() }),
      });
      if (r.status === 201) {
        setWlStatus('done');
        return;
      }
      setWlStatus('error');
      if (r.status === 409) setWlMessage(t('waitlist.duplicate'));
      else if (r.status === 400) setWlMessage(t('waitlist.invalidEmail'));
      else setWlMessage(t('waitlist.genericError'));
    } catch {
      setWlStatus('error');
      setWlMessage(t('waitlist.genericError'));
    }
  }

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  if (!isLoaded) {
    return <div className="min-h-full bg-white flex items-center justify-center text-gray-400">Cargando…</div>;
  }

  return (
    <div className="bg-white text-gray-900 font-sans" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

      {/* ── NAV ─────────────────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200/50">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          {/* Logo — links to vecindus.com */}
          <a href="https://vecindus.com" className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
            <svg width="28" height="28" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="40" height="40" rx="10" fill="#36CCD8"/>
              <path d="M20 8L6 22L12 28L20 20L28 28L34 22L20 8Z" fill="white"/>
              <path d="M20 16L12 24L16 28L20 24L24 28L28 24L20 16Z" fill="white" fillOpacity="0.4"/>
            </svg>
            <span className="text-lg font-bold tracking-tight text-gray-900">Vecindus</span>
          </a>
          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-500">
            <a href="#features" className="hover:text-gray-900 transition-colors">Características</a>
            <a href="#pricing" className="hover:text-gray-900 transition-colors">Precios</a>
            <a href="#testimonials" className="hover:text-gray-900 transition-colors">Casos reales</a>
          </div>
          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors px-1">Iniciar Sesión</Link>
            <Link to="/register" className="bg-[#36CCD8] hover:bg-[#2bb8c5] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all">Comenzar</Link>
          </div>
          {/* Mobile hamburger */}
          <button className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16"/></svg>
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-1">
            <a href="#features" className="block py-2.5 text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Características</a>
            <a href="#pricing" className="block py-2.5 text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Precios</a>
            <a href="#testimonials" className="block py-2.5 text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Casos reales</a>
            <div className="pt-3 border-t border-gray-100 flex flex-col gap-2">
              <Link to="/login" className="py-2.5 text-sm font-medium text-gray-600" onClick={() => setMobileMenuOpen(false)}>Iniciar Sesión</Link>
              <Link to="/register" className="bg-[#36CCD8] text-white text-sm font-semibold px-5 py-2.5 rounded-full text-center" onClick={() => setMobileMenuOpen(false)}>Comenzar</Link>
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ────────────────────────────────────────────────────────────── */}
      <section className="pt-32 pb-24 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#36CCD8] text-sm font-semibold tracking-widest uppercase mb-5">Administración de condominios</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-tight mb-6">
            Tu vida.<br/>Tu comunidad.<br/><span className="text-[#36CCD8]">Vecindus.</span>
          </h1>
          <p className="text-xl sm:text-2xl text-gray-500 font-light max-w-xl mx-auto mb-10 leading-relaxed">
            La plataforma que transforma cómo se administra y se vive en un condominio.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="bg-[#36CCD8] hover:bg-[#2bb8c5] text-white font-semibold px-8 py-4 rounded-full text-lg transition-all shadow-lg shadow-[#36CCD8]/20">
              Probar gratis
            </Link>
            <a href="#features" className="border border-gray-300 hover:border-gray-400 text-gray-700 font-semibold px-8 py-4 rounded-full text-lg transition-all">
              Ver cómo funciona
            </a>
          </div>
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────────────────────── */}
      <section id="features" className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          {features.map((f, i) => (
            <div key={i} className={`flex flex-col md:flex-row items-center gap-16 py-24 ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
              {/* Text */}
              <div className="flex-1">
                <p className="text-[#36CCD8] text-xs font-semibold tracking-widest uppercase mb-4">{f.tag}</p>
                <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-tight whitespace-pre-line mb-6">{f.headline}</h2>
                <p className="text-xl text-gray-500 font-light leading-relaxed">{f.body}</p>
              </div>
              {/* Visual — real app screenshot */}
              <div className="flex-1 flex justify-center">
                <div className="w-full max-w-md">
                  <img src={f.visual} alt={f.alt} loading="lazy" className="w-full rounded-2xl border border-gray-200 shadow-xl shadow-gray-200/60" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── SOCIAL PROOF STRIP ───────────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-[#36CCD8] text-white text-center">
        <div className="max-w-5xl mx-auto">
          <p className="text-white/70 text-sm font-medium tracking-widest uppercase mb-8">Usado por administradores en</p>
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 text-white/60 text-sm font-medium tracking-wide">
            {['México', 'Colombia', 'Venezuela', 'Perú', 'Chile', 'Argentina'].map(c => (
              <span key={c}>{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ────────────────────────────────────────────────────── */}
      <section id="testimonials" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#36CCD8] text-xs font-semibold tracking-widest uppercase mb-4 text-center">Casos reales</p>
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-center mb-16">Lo que dicen los administradores</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div key={i} className={`p-8 rounded-2xl border ${t.highlight ? 'border-[#36CCD8]/40 bg-[#36CCD8]/5' : 'border-gray-200 bg-white'}`}>
                <p className="text-lg font-light text-gray-700 leading-relaxed mb-6">"{t.quote}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{t.author}</p>
                  <p className="text-sm text-gray-500">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING (V1 launch: free plan only; future plans shown as Próximamente, no prices) ── */}
      <section id="pricing" className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#36CCD8] text-xs font-semibold tracking-widest uppercase mb-4">Precios</p>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-4">Simple y transparente</h2>
            <p className="text-xl text-gray-500 font-light">Lanzamos con el plan Gratis — hoy disponible. Los planes de pago llegan pronto; te mostramos ya qué incluirán.</p>
          </div>

          {/* Roadmap strip */}
          <div className="grid sm:grid-cols-3 gap-4 mb-16">
            {roadmap.map(r => (
              <div key={r.title} className={`rounded-2xl p-6 border ${r.tone === 'done' ? 'border-[#36CCD8]/50 bg-[#36CCD8]/5' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${r.tone === 'done' ? 'bg-[#36CCD8]' : r.tone === 'next' ? 'bg-amber-400' : 'bg-gray-300'}`} />
                  <p className="text-xs font-semibold tracking-widest uppercase text-gray-500">{r.label}</p>
                </div>
                <p className="font-semibold text-gray-900">{r.title}</p>
                <p className="text-sm text-gray-500">{r.detail}</p>
              </div>
            ))}
          </div>

          {/* Free plan — available now */}
          <div className="max-w-md mx-auto bg-[#36CCD8] text-white rounded-3xl p-8 mb-16 relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-[#36CCD8] text-xs font-bold px-4 py-1.5 rounded-full shadow whitespace-nowrap">disponible ahora</div>
            <h3 className="text-sm font-semibold tracking-widest uppercase text-white/70 mb-2">Plan Gratis</h3>
            <p className="text-5xl font-semibold tracking-tight mb-1">$0</p>
            <p className="text-white/70 text-sm mb-8">para tu condominio. Sin tarjeta, sin compromiso.</p>
            <ul className="space-y-3 text-sm text-white/85 mb-10">
              {['Comunicación Administrador–Residente', 'Votaciones, avisos y calendario', 'Amenidades y pagos de mantenimiento', 'Quejas y sugerencias', 'Gestión de proveedores'].map(l => <li key={l} className="flex items-start gap-2"><span>✓</span>{l}</li>)}
            </ul>
            <Link to="/register" className="block text-center bg-white text-[#36CCD8] font-semibold px-6 py-3.5 rounded-full text-sm">Comenzar gratis</Link>
          </div>

          {/* Comparison table — responsive (scrolls on mobile, stacks on phones) */}
          <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight text-center mb-4">Qué incluye cada plan</h3>
          <p className="text-center text-gray-500 mb-10">Por tipo de usuario. Los precios de los planes de pago se anunciarán al lanzarlos.</p>

          {/* Tablet/desktop: real table with horizontal scroll fallback */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full min-w-[720px] bg-white rounded-2xl border border-gray-200 overflow-hidden text-sm">
              <thead>
                <tr>
                  <th className="text-left px-5 py-4 text-xs font-semibold tracking-widest uppercase text-gray-400 border-b border-gray-100">Tipo de usuario</th>
                  {planColumns.map(c => (
                    <th key={c.id} className="px-5 py-4 border-b border-gray-100 text-left">
                      <p className="font-semibold text-gray-900">{c.name}</p>
                      <span className={`inline-block mt-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full ${c.status === 'disponible' ? 'bg-[#36CCD8]/10 text-[#2bb8c5]' : 'bg-amber-100 text-amber-700'}`}>{c.status === 'disponible' ? 'Disponible' : 'Próximamente'}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {planColumns[0].users.map((u, ui) => (
                  <tr key={u.type} className={ui % 2 === 1 ? 'bg-gray-50/60' : ''}>
                    <td className="align-top px-5 py-4 font-semibold text-gray-900 border-b border-gray-100">{u.type}</td>
                    {planColumns.map(c => (
                      <td key={c.id} className="align-top px-5 py-4 border-b border-gray-100">
                        <ul className="space-y-1.5 text-gray-600">
                          {c.users[ui].items.map(it => <li key={it} className="flex items-start gap-1.5"><span className="text-[#36CCD8] mt-0.5">✓</span>{it}</li>)}
                        </ul>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: stacked plan cards with same data */}
          <div className="sm:hidden space-y-6">
            {planColumns.map(c => (
              <div key={c.id} className={`rounded-2xl border p-6 ${c.status === 'disponible' ? 'border-[#36CCD8]/50 bg-white' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-semibold text-gray-900">{c.name}</p>
                  <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${c.status === 'disponible' ? 'bg-[#36CCD8]/10 text-[#2bb8c5]' : 'bg-amber-100 text-amber-700'}`}>{c.status === 'disponible' ? 'Disponible' : 'Próximamente'}</span>
                </div>
                <p className="text-sm text-gray-500 mb-4">{c.blurb}</p>
                {c.users.map(u => (
                  <div key={u.type} className="mb-4 last:mb-0">
                    <p className="text-xs font-semibold tracking-wide uppercase text-gray-400 mb-2">{u.type}</p>
                    <ul className="space-y-1.5 text-sm text-gray-600">
                      {u.items.map(it => <li key={it} className="flex items-start gap-1.5"><span className="text-[#36CCD8] mt-0.5">✓</span>{it}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WAITING LIST (launch-readiness item 1, resumed 2026-10-01) ──────────────────── */}
      <section id="lista-de-espera" className="py-24 px-6 bg-gray-50">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-[#36CCD8] text-xs font-semibold tracking-widest uppercase mb-4">{t('waitlist.badge')}</p>
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-4">{t('waitlist.title')}</h2>
          <p className="text-xl text-gray-500 font-light mb-10">{t('waitlist.subtitle')}</p>
          {wlStatus === 'done' ? (
            <div className="p-8 rounded-2xl border border-[#36CCD8]/40 bg-[#36CCD8]/5" aria-live="polite">
              <p className="text-5xl mb-4">🎉</p>
              <p className="text-lg font-semibold text-gray-900 mb-1">{t('waitlist.success')}</p>
              <p className="text-gray-500">{t('waitlist.successDetail')}</p>
            </div>
          ) : (
            <form onSubmit={submitWaitingList} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <label htmlFor="wl-email" className="sr-only">{t('waitlist.emailLabel')}</label>
              <input
                id="wl-email"
                type="email"
                required
                value={wlEmail}
                onChange={e => setWlEmail(e.target.value)}
                placeholder={t('waitlist.emailPlaceholder')}
                className="flex-1 border border-gray-300 rounded-full px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#36CCD8]/40"
              />
              <button
                type="submit"
                disabled={wlStatus === 'sending'}
                className="bg-[#36CCD8] hover:bg-[#2bb8c5] disabled:opacity-60 text-white font-semibold px-8 py-3.5 rounded-full text-sm transition-all shadow-lg shadow-[#36CCD8]/20 whitespace-nowrap"
              >
                {wlStatus === 'sending' ? t('waitlist.submitting') : t('waitlist.submit')}
              </button>
            </form>
          )}
          {wlStatus === 'error' && <p className="text-sm text-red-500 mt-4" aria-live="polite">{wlMessage}</p>}
          {wlStatus !== 'done' && <p className="text-xs text-gray-400 mt-4">{t('waitlist.privacyNote')}</p>}
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-6">Tu condominio merece algo mejor.</h2>
          <p className="text-xl text-gray-500 font-light mb-10">Empieza hoy. Sin tarjeta, sin compromiso.</p>
          <Link to="/register" className="inline-block bg-[#36CCD8] hover:bg-[#2bb8c5] text-white font-semibold px-10 py-4 rounded-full text-lg transition-all shadow-lg shadow-[#36CCD8]/20">
            Crear mi condominio gratis
          </Link>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer className="border-t border-gray-200 py-12 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#36CCD8" strokeWidth="2.2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            <span className="font-semibold text-gray-900">Vecindus</span>
          </div>
          <p className="text-sm text-gray-400">© 2026 Vecindus. México · Colombia · Venezuela · Latinoamérica.</p>
          <div className="flex gap-6 text-sm text-gray-500">
            <a href="/privacidad" className="hover:text-gray-900 transition-colors">Privacidad</a>
            <a href="/terminos" className="hover:text-gray-900 transition-colors">Términos</a>
            <a href="#" className="hover:text-gray-900 transition-colors">Contacto</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
